// src/pages/AdminProcedures.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useOrganization } from "../contexts/OrganizationContext";
import { OrganizationService } from "../services/organizationService";

const skillLevels = [
  "未経験",
  "見学済み",
  "経験1回",
  "経験2回",
  "経験3回以上",
  "外から見守り",
  "独り立ち",
  "指導可",
];

function AdminProcedures() {
  const { currentOrganization } = useOrganization();
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [skillMap, setSkillMap] = useState({});
  const [loading, setLoading] = useState(true);
  const user = getAuth().currentUser;

  useEffect(() => {
    const fetchMasterData = async () => {
      if (!currentOrganization?.id) {
        setLoading(false);
        return;
      }

      try {
        // 組織配下のdepartmentsとproceduresを取得
        const deptCollection = OrganizationService.getOrgCollection(
          currentOrganization.id,
          "departments"
        );
        const procCollection = OrganizationService.getOrgCollection(
          currentOrganization.id,
          "procedures"
        );

        const [deptSnap, procSnap] = await Promise.all([
          getDocs(deptCollection),
          getDocs(procCollection)
        ]);

        const depts = deptSnap.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        const procs = procSnap.docs.map((doc) => ({ 
          id: doc.id, 
          ...doc.data() 
        }));

        setDepartments(depts);
        setProcedures(procs);
      } catch (error) {
        console.error("マスタデータの取得エラー:", error);
        alert("データの取得に失敗しました: " + error.message);
      }
    };

    const fetchSkillRecords = async () => {
      if (!user || !currentOrganization?.id) return;

      try {
        // 組織配下のskillRecordsを取得
        const skillRecordsCollection = OrganizationService.getOrgCollection(
          currentOrganization.id,
          "skillRecords"
        );
        const q = query(
          skillRecordsCollection,
          where("userId", "==", user.uid)
        );
        const snapshot = await getDocs(q);
        
        const data = {};
        snapshot.docs.forEach((doc) => {
          data[doc.data().procedureId] = doc.data().level;
        });
        setSkillMap(data);
      } catch (error) {
        console.error("スキルレコードの取得エラー:", error);
      }
    };

    const loadData = async () => {
      setLoading(true);
      await fetchMasterData();
      await fetchSkillRecords();
      setLoading(false);
    };

    loadData();
  }, [user, currentOrganization]);

  const handleChange = async (procedureId, level) => {
    if (!user || !currentOrganization?.id) return;

    try {
      // 組織配下のskillRecordsに保存
      const recordRef = OrganizationService.getOrgDoc(
        currentOrganization.id,
        "skillRecords",
        `${user.uid}_${procedureId}`
      );
      
      await setDoc(recordRef, {
        userId: user.uid,
        procedureId,
        level,
        organizationId: currentOrganization.id,
        updatedAt: new Date()
      });
      
      setSkillMap((prev) => ({ ...prev, [procedureId]: level }));
    } catch (error) {
      console.error("スキルレコードの保存エラー:", error);
      alert("スキルの更新に失敗しました: " + error.message);
    }
  };

  // 組織が選択されていない場合
  if (!currentOrganization?.id) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2 style={{ fontSize: "1.5rem", margin: "1rem" }}>
          📝 術式ごとのスキル進捗登録
        </h2>
        <p style={{ color: "#666" }}>組織を選択してください</p>
      </div>
    );
  }

  // ローディング中
  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2 style={{ fontSize: "1.5rem", margin: "1rem" }}>
          📝 術式ごとのスキル進捗登録
        </h2>
        <p style={{ color: "#666" }}>読み込み中...</p>
      </div>
    );
  }

  return (
    <div>      
      <h2 style={{ textAlign: "center", fontSize: "1.5rem", margin: "1rem" }}>
        📝 術式ごとのスキル進捗登録
      </h2>
      
      <div style={{ padding: "1rem" }}>
        {departments.length === 0 && (
          <div style={{ 
            textAlign: "center", 
            padding: "2rem", 
            color: "#666" 
          }}>
            診療科が登録されていません
          </div>
        )}

        {departments.map((dept) => {
          const deptProcedures = procedures.filter(
            (p) => p.departmentId === dept.id
          );
          
          if (deptProcedures.length === 0) return null;

          return (
            <div key={dept.id} style={{ marginBottom: "2rem" }}>
              <h3 style={{ marginBottom: "0.5rem" }}>{dept.name}</h3>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f0f0f0" }}>
                    <th style={{ 
                      padding: "0.5rem", 
                      border: "1px solid #ccc" 
                    }}>
                      術式名
                    </th>
                    <th style={{ 
                      padding: "0.5rem", 
                      border: "1px solid #ccc" 
                    }}>
                      現在のステータス
                    </th>
                    <th style={{ 
                      padding: "0.5rem", 
                      border: "1px solid #ccc" 
                    }}>
                      レベル選択
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deptProcedures.map((proc) => (
                    <tr key={proc.id}>
                      <td style={{ 
                        padding: "0.5rem", 
                        border: "1px solid #ccc" 
                      }}>
                        {proc.name}
                      </td>
                      <td style={{ 
                        padding: "0.5rem", 
                        border: "1px solid #ccc",
                        textAlign: "center",
                        fontWeight: "bold"
                      }}>
                        {skillMap[proc.id] || "未経験"}
                      </td>
                      <td style={{ 
                        padding: "0.5rem", 
                        border: "1px solid #ccc" 
                      }}>
                        <select
                          value={skillMap[proc.id] || "未経験"}
                          onChange={(e) => handleChange(proc.id, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "0.3rem",
                            border: "1px solid #ccc",
                            borderRadius: "4px"
                          }}
                        >
                          {skillLevels.map((level) => (
                            <option key={level} value={level}>
                              {level}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AdminProcedures;